import type { ViewsService } from '../../Views.service';
import type { CasePhaseData } from '../../CasePhaseData/CasePhaseData.ts';
import type { ImagesService } from '../images/Images.service';
import type { AppController } from '../../App.controller';

/**
 * Represents a PhaseSwitcher that allows switching between different phases in a cardiac application.
 */
export class PhaseSwitcher {
  private readonly _appController: AppController;

  private readonly _viewsService: ViewsService;

  private readonly _casePhaseData: CasePhaseData;

  private readonly _imagesService: ImagesService;

  public constructor(
    appController: AppController,
    viewsService: ViewsService,
    casePhaseData: CasePhaseData,
    imagesService: ImagesService
  ) {
    this._appController = appController;
    this._viewsService = viewsService;
    this._casePhaseData = casePhaseData;
    this._imagesService = imagesService;
  }

  private get _isImagesLoadingCompleted(): boolean {
    return this._imagesService.isImagesLoadingCompleted;
  }

  public async switchPhaseByKind(kind: 'endSystole' | 'endDiastole'): Promise<void> {
    const imageStackDescriptor = this._viewsService.currentView?.getImageStackDescriptor();
    if (!imageStackDescriptor) {
      throw new Error('Image stack descriptor has to be defined');
    }

    const phaseIndex = this._casePhaseData.getParam(imageStackDescriptor, kind + 'Index');
    if (phaseIndex === undefined) {
      throw new Error('Phase index has to be defined');
    }

    await this.setPhaseByIndex(phaseIndex);
  }

  public setPhaseByIndex(index: number): Promise<void> {
    if (!this._isImagesLoadingCompleted) {
      return Promise.resolve();
    }
    const currentView = this._viewsService.currentView;
    if (!currentView) {
      throw new Error('Current view has to be defined');
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
    this._appController.changePhaseAsync(currentView.getImageStackDescriptor(), index);
    return Promise.resolve();
  }
}
